import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';

import useGameDetails from './useGameDetails.js';
import { useNewestRoundID } from './rounds.js';
import { GetDeepComparedData } from 'utils/Query/index.js';


const DUMMY_CLAIMABILITY_DATA = { claimable: false, isRefund: false }
const ROUND_ID_TOO_OLD_TO_UPDATE_THRESHOLD = 288 // approximately 24 hours worth of 5-minute rounds


export function useIsClaimable_historyRound(roundID, alreadyKnownToBeClaimed) {
  if (roundID === undefined) throw new SyntaxError('missing required argument: roundID');

  const gameDetails = useGameDetails();
  const currentEpoch = useNewestRoundID();
  const account = useAccount();

  const refetchInterval = useMemo(GetRefetchInterval
    , [currentEpoch, roundID])

  const { data: claimabilityData, refetch: ForceRefetch, isFetching } = useQuery({
    queryKey: ['roundIsClaimable', roundID, gameDetails.contracts.prediction?._address, gameDetails.wantedChain.id],
    queryFn: FetchRoundClaimabilityData,
    placeholderData: DUMMY_CLAIMABILITY_DATA,
    refetchInterval: refetchInterval,
    staleTime: 10 * 60 * 1000,
    cacheTime: 4 * 60 * 60 * 1000,
    refetchOnMount: !!refetchInterval,
    structuralSharing: GetDeepComparedData
  });

  useEffect(() => {
    if (!claimabilityData.claimable && !isFetching)
      ForceRefetch();
  }, [refetchInterval, isFetching, claimabilityData, ForceRefetch]);


  async function FetchRoundClaimabilityData() {
    let claimabilityData = { ...DUMMY_CLAIMABILITY_DATA };
    if (!roundID || alreadyKnownToBeClaimed || !gameDetails.contracts.prediction || !account?.address)
      return claimabilityData;

    const contract = gameDetails.contracts.prediction;
    const refundable = await contract.methods.Refundable(roundID, account?.address).call();
    const claimable = await contract.methods.Claimable(roundID, account?.address).call();


    if (claimable || refundable) {
      claimabilityData.claimable = true;
      if (refundable)
        claimabilityData.isRefund = true;
    }
    return claimabilityData;
  }

  function GetRefetchInterval() {
    const roundTooOld = (roundID + ROUND_ID_TOO_OLD_TO_UPDATE_THRESHOLD < currentEpoch);
    if (roundTooOld || !currentEpoch || !roundID)
      return 0; // no refetching
    else
      return 10 * 1000;
  }

  return claimabilityData;
}
